/**
 * The client half of the assistant's "show me where" contract (brief
 * ASSIST-JUDGEMENT-NAV, Part 2). A `navigate` card may carry
 * `spotlight: {anchor, tip, requires?}`; clicking the card arms a spotlight
 * for that anchor and `AssistSpotlight` rings the element tagged
 * `data-assist="<anchor>"` once the page shows it.
 *
 * Everything the model hands over is re-checked here: an anchor that is not
 * a plain registry id, or a tip that is not a short string, is dropped and
 * the card falls back to a plain navigation.
 */

/** Registry ids are lower-case words joined by `.` and `_` (manifest schema). */
export const ANCHOR_ID_RE = /^[a-z0-9_.]+$/

/** Longest tip the ring's bubble shows (brief: `tip ≤80`). */
export const MAX_TIP_LENGTH = 80

/** Preconditions an anchor may declare; only edit mode today. */
export const REQUIRES_EDIT_MODE = 'edit_mode'

/** The anchor a `requires: 'edit_mode'` spotlight points at first. */
export const EDIT_TOGGLE_ANCHOR = 'dashboard.edit_toggle'

/**
 * Spread onto the Edit toggle while edit mode is on (it reads "Done" then),
 * so the spotlight can tell edit mode is already on and go straight to an
 * edit-mode anchor instead of asking for Edit (which would turn it off).
 */
export const EDIT_MODE_ON_PROPS = Object.freeze({ 'data-assist-state': 'edit_mode' })

/** Viewport margin the tip keeps, in px. */
export const TIP_EDGE_PX = 8
/** Gap between the control's edge and the ring, in px. */
export const RING_GAP_PX = 4
/** Gap between the ring and the tip bubble, in px. */
export const TIP_GAP_PX = 8

/** How long the spotlight waits for the anchor to mount on the new page. */
export const ANCHOR_WAIT_MS = 4000

/** How long a found ring stays up before it dismisses itself. */
export const RING_DISMISS_MS = 8000

/** How long an armed spotlight waits for the route to land on its page. */
export const ARM_TTL_MS = 10000

/** DOM event the miss log is published on, for a collector to pick up later. */
export const ASSIST_TELEMETRY_EVENT = 'graphit:assist-telemetry'

function isAnchorId(value) {
  return typeof value === 'string' && ANCHOR_ID_RE.test(value)
}

/**
 * @param {unknown} raw - `payload.spotlight` from a navigate card
 * @returns {{anchor: string, tip: string, requires?: string, parent?: string}|null}
 */
export function normalizeSpotlight(raw) {
  if (!raw || typeof raw !== 'object') return null
  const { anchor, tip, requires, parent } = raw
  if (!isAnchorId(anchor)) return null
  const cleanTip = typeof tip === 'string' ? tip.trim().slice(0, MAX_TIP_LENGTH) : ''
  return {
    anchor,
    tip: cleanTip,
    ...(requires === REQUIRES_EDIT_MODE ? { requires } : {}),
    ...(isAnchorId(parent) && parent !== anchor ? { parent } : {}),
  }
}

/**
 * @param {string} anchor - a validated registry id
 * @returns {string} the attribute selector for it
 */
export function anchorSelector(anchor) {
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(anchor) : anchor
  return `[data-assist="${escaped}"]`
}

/**
 * Whether the element is rendered and not hidden (a `hidden md:flex` rail
 * button below `md`, a collapsed section). `checkVisibility` where the
 * browser has it, else "has a layout box".
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isVisible(element) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true })
  return element.getClientRects().length > 0
}

/**
 * The first visible element tagged with the anchor. One id can be on more
 * than one element (the empty-state and floating "Add widget" buttons), and
 * some copies are hidden at the current width (manifest schema).
 *
 * @param {string} anchor
 * @param {ParentNode} [root]
 * @returns {HTMLElement|null}
 */
export function findAnchor(anchor, root = document) {
  if (!isAnchorId(anchor)) return null
  for (const element of root.querySelectorAll(anchorSelector(anchor))) {
    if (isVisible(element)) return element
  }
  return null
}

/**
 * The pathname part of a card's `url_path` (no `?query`, no `#fragment`, no trailing
 * slash) so it can be compared with `location.pathname`.
 *
 * @param {string} path
 * @returns {string}
 */
export function routeOf(path) {
  const bare = String(path || '').split(/[?#]/)[0]
  return bare.length > 1 ? bare.replace(/\/+$/, '') : bare
}

/**
 * Whether the dashboard is already in edit mode: its Edit toggle is on
 * screen and carries `EDIT_MODE_ON_PROPS`.
 *
 * @returns {boolean}
 */
export function isEditModeOn() {
  const toggle = findAnchor(EDIT_TOGGLE_ANCHOR)
  return toggle?.getAttribute('data-assist-state') === EDIT_MODE_ON_PROPS['data-assist-state']
}

/**
 * Where the tip goes: under the ringed control, or above it when there is
 * no room below (and there is above), never on top of the control;
 * horizontally clamped so it stays `TIP_EDGE_PX` inside the viewport.
 *
 * @param {{top: number, bottom: number, left: number}} rect - the control's viewport rect
 * @param {{width: number, height: number}} tip - the tip's measured size
 * @param {{width: number, height: number}} viewport
 * @returns {{top: number, left: number, placement: 'below'|'above'}}
 */
export function placeTip(rect, tip, viewport) {
  const offset = RING_GAP_PX + TIP_GAP_PX
  const below = rect.bottom + offset
  const above = rect.top - offset - tip.height
  const fitsBelow = below + tip.height <= viewport.height - TIP_EDGE_PX
  const placement = !fitsBelow && above >= TIP_EDGE_PX ? 'above' : 'below'
  const maxLeft = viewport.width - tip.width - TIP_EDGE_PX
  const left = Math.max(TIP_EDGE_PX, Math.min(rect.left, maxLeft))
  return { top: placement === 'above' ? above : below, left, placement }
}

/**
 * Calls `find()` now and on every DOM change until it returns something or
 * `timeoutMs` passes. Returns a cancel function.
 *
 * @param {{find: () => any, timeoutMs: number, onFound: (hit: any) => void, onTimeout: () => void}} opts
 * @returns {() => void}
 */
export function watchForAnchor({ find, timeoutMs, onFound, onTimeout }) {
  let done = false
  let observer = null
  let timer = null
  const stop = () => {
    done = true
    observer?.disconnect()
    clearTimeout(timer)
  }
  const check = () => {
    if (done) return
    const hit = find()
    if (!hit) return
    stop()
    onFound(hit)
  }
  check()
  if (done) return stop
  observer = new MutationObserver(check)
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-assist', 'hidden', 'style', 'class'] })
  timer = setTimeout(() => {
    if (done) return
    stop()
    onTimeout()
  }, timeoutMs)
  return stop
}

/**
 * Logs an anchor the page never showed (`assist_anchor_miss`): hidden by
 * role, an empty state, a collapsed menu, or a registry that drifted from
 * the UI. A `console.warn` for developers plus a DOM event a telemetry
 * collector can forward to the backend later; never throws.
 *
 * @param {{anchor: string, path: string, reason: string}} detail
 */
export function logAssistMiss(detail) {
  const event = { event: 'assist_anchor_miss', at: new Date().toISOString(), ...detail }
  try {
    console.warn('[assist] assist_anchor_miss', event)
    window.dispatchEvent(new CustomEvent(ASSIST_TELEMETRY_EVENT, { detail: event }))
  } catch {
    // Telemetry must never break the page.
  }
}
